import type { CSSProperties } from 'react';

/**
 * Turns a CSS declaration string into a React style object.
 *
 * The design prototype styles every element with a `style="…"` attribute, and
 * those strings are carried over here unchanged so the rendered result matches
 * the design exactly rather than being re-typed as camelCase objects. Results
 * are cached, so a string appearing in a list of two hundred rows is parsed once.
 */
const cache = new Map<string, CSSProperties>();

function toCamel(prop: string): string {
  const p = prop.trim();
  if (p.startsWith('--')) return p; // custom property, kept as-is
  // -webkit-appearance → WebkitAppearance; -moz-x → MozX
  const normalised = p.startsWith('-') ? p.slice(1) : p;
  const camel = normalised.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
  return p.startsWith('-') ? camel.charAt(0).toUpperCase() + camel.slice(1) : camel;
}

export function sx(css: string): CSSProperties {
  const hit = cache.get(css);
  if (hit) return hit;

  const out: Record<string, string> = {};
  let depth = 0;
  let start = 0;
  const push = (chunk: string) => {
    const decl = chunk.trim();
    if (!decl) return;
    const i = decl.indexOf(':');
    if (i < 1) return;
    const prop = decl.slice(0, i);
    const value = decl.slice(i + 1).trim();
    if (!value) return;
    out[toCamel(prop)] = value;
  };
  for (let i = 0; i < css.length; i++) {
    const ch = css[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ';' && depth === 0) {
      push(css.slice(start, i));
      start = i + 1;
    }
  }
  push(css.slice(start));

  const style = out as CSSProperties;
  cache.set(css, style);
  return style;
}

/** Composes a base style string with extra declarations, e.g. a computed colour. */
export function sxc(...parts: (string | false | null | undefined)[]): CSSProperties {
  return sx(parts.filter(Boolean).join(';'));
}
